import React, { useState, useEffect } from 'react';
import { Globe, Trash2, Check, Loader2, RefreshCw, ExternalLink, Info, AlertTriangle, X, Copy, Clock, CheckCircle2 } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

interface DnsInstructions {
  main_cname: {
    name: string;
    target: string;
    description: string;
  };
  acme_cname?: {
    name: string;
    target: string;
    description: string;
  };
}

interface AgencyDomain {
  id: number;
  domain: string;
  status: string;
  cloudflare_proxy_enabled: boolean;
  cname_target: string | null;
  delegated_acme_subdomain: string | null;
  dns_instructions: DnsInstructions | null;
  dns_validated_at: string | null;
  ssl_issued_at: string | null;
  last_validation_check: string | null;
  validation_errors: string | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  agencyId: string;
  agencyName: string;
}

const AgencyDomainsTab: React.FC<Props> = ({ agencyId, agencyName }) => {
  const { showSuccess, showError } = useToast();
  const [domains, setDomains] = useState<AgencyDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [hoveredDomain, setHoveredDomain] = useState<number | null>(null);
  const [expandedDomain, setExpandedDomain] = useState<number | null>(null);
  
  // Delete modal state
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; domain: AgencyDomain | null }>({ open: false, domain: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Check if agency already has a domain configured
  const hasExistingDomain = domains.length > 0;
  const existingDomain = domains[0] || null;

  useEffect(() => {
    loadDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId]);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/agencies/${agencyId}/domains`);
      setDomains(response.data);
      // Auto-expand if there's a pending domain
      if (response.data.length > 0 && response.data[0].status !== 'active') {
        setExpandedDomain(response.data[0].id);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await apiService.get(`/agencies/${agencyId}/domains`);
      setDomains(response.data);
      showSuccess('Status refreshed');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      showError('Please enter a domain');
      return;
    }

    const domainRegex = /^[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i;    
    if (!domainRegex.test(newDomain)) {
      showError('Invalid domain format');
      return;
    }

    try {
      setAdding(true);
      const addedDomainName = newDomain.toLowerCase();
      await apiService.post(`/agencies/${agencyId}/domains`, { domain: newDomain });
      showSuccess(`Domain ${newDomain} added successfully`);
      setNewDomain('');
      const response = await apiService.get(`/agencies/${agencyId}/domains`);
      setDomains(response.data);
      const newlyAdded = response.data.find((d: AgencyDomain) => d.domain === addedDomainName);
      if (newlyAdded) {
        setExpandedDomain(newlyAdded.id);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const openDeleteModal = (domain: AgencyDomain) => {
    setDeleteModal({ open: true, domain });
    setDeleteConfirmText('');
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, domain: null });
    setDeleteConfirmText('');
    setDeleting(false);
  };

  const handleDeleteDomain = async () => {
    if (!deleteModal.domain) return;

    setDeleting(true);
    try {
      await apiService.delete(`/agencies/${agencyId}/domains/${deleteModal.domain.id}`);
      showSuccess('Domain removed successfully');
      closeDeleteModal();
      loadDomains();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to remove domain');
      setDeleting(false);
    }
  };

  const copyToClipboard = (text: string, label?: string) => {
    navigator.clipboard.writeText(text);
    showSuccess(label ? `${label} copied to clipboard` : 'Copied to clipboard');
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; color: string; text: string; icon?: React.ReactNode }> = {
      active: { bg: 'var(--v2-success-bg)', color: 'var(--v2-success)', text: 'Active', icon: <CheckCircle2 size={12} /> },
      pending_dns: { bg: 'var(--v2-warning-bg)', color: '#92400e', text: 'Pending DNS', icon: <Clock size={12} /> },
      pending_cname: { bg: 'var(--v2-warning-bg)', color: '#92400e', text: 'Awaiting DNS Setup', icon: <Clock size={12} /> },
      validating: { bg: 'var(--v2-info-bg)', color: 'var(--v2-primary)', text: 'Validating DNS', icon: <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> },
      pending_ssl: { bg: 'var(--v2-warning-bg)', color: '#92400e', text: 'Issuing SSL Certificate', icon: <Clock size={12} /> },
      deleting: { bg: 'var(--v2-error-bg)', color: 'var(--v2-error)', text: 'Removing', icon: <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> },
      failed: { bg: 'var(--v2-error-bg)', color: 'var(--v2-error)', text: 'Failed', icon: <AlertTriangle size={12} /> },
      provisioning: { bg: 'var(--v2-info-bg)', color: 'var(--v2-primary)', text: 'Provisioning', icon: <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> }
    };

    const style = styles[status] || { bg: 'var(--v2-surface-alt)', color: 'var(--v2-text-muted)', text: status };

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 10px',
        backgroundColor: style.bg,
        color: style.color,
        borderRadius: 'var(--radius-full)',
        fontSize: '0.75rem',
        fontWeight: 600
      }}>
        {style.icon}
        {style.text}
      </span>
    );
  };

  const inputStyle = (focused: boolean, disabled: boolean = false): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    backgroundColor: disabled ? 'var(--v2-surface-alt)' : 'var(--v2-surface)',
    border: `1px solid ${focused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9375rem',
    color: disabled ? 'var(--v2-text-muted)' : 'var(--v2-text)',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
    cursor: disabled ? 'not-allowed' : 'text'
  });

  const buttonStyle = (hovered: boolean, variant: 'primary' | 'secondary' | 'danger' = 'primary', disabled: boolean = false): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: disabled 
      ? 'var(--v2-border)'
      : variant === 'primary'
        ? hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)'
        : variant === 'danger'
          ? hovered ? '#b91c1c' : 'var(--v2-error)'
          : hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    color: disabled ? 'var(--v2-text-muted)' : variant === 'secondary' ? 'var(--v2-text)' : '#ffffff',
    border: variant === 'secondary' ? '1px solid var(--v2-border)' : 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap' as const
  });

  const iconButtonStyle = (hovered: boolean, danger?: boolean): React.CSSProperties => ({
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    backgroundColor: hovered ? (danger ? 'var(--v2-error-bg)' : 'var(--v2-surface-hover)') : 'transparent',
    color: hovered && danger ? 'var(--v2-error)' : 'var(--v2-text-tertiary)',
    transition: 'background-color 0.15s ease, color 0.15s ease',
  });

  const copyButtonStyle = (hovered: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: hovered ? 'var(--v2-primary)' : 'var(--v2-surface)',
    color: hovered ? '#ffffff' : 'var(--v2-text-secondary)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  });

  const isDeleteConfirmValid = deleteConfirmText.toLowerCase() === 'delete';

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Loader2 size={24} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} color="var(--v2-primary)" />
        <p style={{ color: 'var(--v2-text-tertiary)', marginTop: '12px' }}>Loading domain configuration...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Info Box - Different content based on state */}
      <div style={{
        padding: '16px',
        borderRadius: 'var(--radius-md)',
        borderLeft: '4px solid var(--v2-primary)',
        backgroundColor: 'var(--v2-info-bg)',
        marginBottom: '24px',
        display: 'flex',
        gap: '12px',
      }}>
        <Info size={20} strokeWidth={1.5} color="var(--v2-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: 'var(--v2-primary)', fontSize: '0.875rem' }}>
            Custom Domain for Your Agency Portal
          </p>
          {!hasExistingDomain ? (
            <p style={{ margin: 0, color: 'var(--v2-text-secondary)', fontSize: '0.8125rem', lineHeight: 1.6 }}>
              Add a custom domain to give your clients a branded experience. Instead of accessing your portal at <code style={{ backgroundColor: 'var(--v2-bg)', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8125rem' }}>my.dashboardfox.app</code>, they'll use your own domain like <code style={{ backgroundColor: 'var(--v2-bg)', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8125rem' }}>portal.yourcompany.com</code>.
            </p>
          ) : existingDomain?.status === 'active' ? (
            <p style={{ margin: 0, color: 'var(--v2-text-secondary)', fontSize: '0.8125rem', lineHeight: 1.6 }}>
              Your custom domain is active. Your clients can access the portal at <strong>{existingDomain.domain}</strong>. Each agency can have one custom domain. To use a different domain, remove the current one first.
            </p>
          ) : (
            <p style={{ margin: 0, color: 'var(--v2-text-secondary)', fontSize: '0.8125rem', lineHeight: 1.6 }}>
              Complete the DNS setup below to activate your custom domain. Both DNS records are required — the second one allows us to automatically issue and renew your SSL certificate.
            </p>
          )}
        </div>
      </div>

      {/* Add Domain Section */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Globe size={20} strokeWidth={1.5} color="var(--v2-primary)" />
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>
              {hasExistingDomain ? 'Custom Domain' : 'Add Custom Domain'}
            </h3>
          </div>
          {hasExistingDomain && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              onMouseEnter={() => setHoveredButton('refresh')}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                ...buttonStyle(hoveredButton === 'refresh', 'secondary', refreshing),
                padding: '8px 14px',
              }}
            >
              <RefreshCw size={16} strokeWidth={1.5} style={refreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
              Check Status
            </button>
          )}
        </div>

        {/* Show message if domain already exists */}
        {hasExistingDomain && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'var(--v2-surface-alt)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--v2-border)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Info size={16} strokeWidth={1.5} color="var(--v2-text-muted)" />
            <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)' }}>
              Each agency can have one custom domain. To use a different domain, remove <strong>{existingDomain?.domain}</strong> first.
            </span>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
              onFocus={() => setFocusedInput('domain')}
              onBlur={() => setFocusedInput(null)}
              placeholder={hasExistingDomain ? "Remove existing domain first" : "portal.yourcompany.com"}
              disabled={adding || hasExistingDomain}
              style={inputStyle(focusedInput === 'domain', hasExistingDomain)}
              onKeyDown={(e) => e.key === 'Enter' && !hasExistingDomain && handleAddDomain()}
            />
          </div>
          <button
            onClick={handleAddDomain}
            disabled={adding || !newDomain.trim() || hasExistingDomain}
            onMouseEnter={() => setHoveredButton('add')}
            onMouseLeave={() => setHoveredButton(null)}
            style={buttonStyle(hoveredButton === 'add', 'primary', adding || !newDomain.trim() || hasExistingDomain)}
          >
            {adding ? (
              <>
                <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                Adding...
              </>
            ) : (
              'Add Domain'
            )}
          </button>
        </div>

        {!hasExistingDomain && (
          <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', marginTop: '12px', margin: '12px 0 0 0' }}>
            Enter the domain you want to use (e.g., portal.yourcompany.com or dashboard.yourcompany.com)
          </p>
        )}
      </div>

      {/* Domain Card */}
      {existingDomain && (
        <div
          onMouseEnter={() => setHoveredDomain(existingDomain.id)}
          onMouseLeave={() => setHoveredDomain(null)}
          style={{
            backgroundColor: hoveredDomain === existingDomain.id ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--v2-border)',
            overflow: 'hidden',
            transition: 'background-color 0.15s ease'
          }}
        >
          {/* Domain Header */}
          <div
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: expandedDomain === existingDomain.id ? '1px solid var(--v2-border)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => setExpandedDomain(expandedDomain === existingDomain.id ? null : existingDomain.id)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', fontFamily: 'var(--font-mono)' }}>
                {existingDomain.domain}
              </span>
              {getStatusBadge(existingDomain.status)}
            </div>                
            <div style={{ display: 'flex', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
              {existingDomain.status === 'active' && (
                <a
                  href={`https://${existingDomain.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={iconButtonStyle(hoveredButton === `open-${existingDomain.id}`)}
                  onMouseEnter={() => setHoveredButton(`open-${existingDomain.id}`)}
                  onMouseLeave={() => setHoveredButton(null)}
                  title="Open domain in new tab"
                >
                  <ExternalLink size={16} strokeWidth={1.5} />
                </a>
              )}
              <button
                onClick={() => openDeleteModal(existingDomain)}
                onMouseEnter={() => setHoveredButton(`delete-${existingDomain.id}`)}
                onMouseLeave={() => setHoveredButton(null)}
                style={iconButtonStyle(hoveredButton === `delete-${existingDomain.id}`, true)}
                title="Remove domain"
              >
                <Trash2 size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* Expanded Content */}
          {(expandedDomain === existingDomain.id || existingDomain.status !== 'active') && (
            <div style={{ padding: '20px' }}>
              {/* DNS Instructions */}
              {existingDomain.dns_instructions && existingDomain.status !== 'active' && (
                <div style={{
                  backgroundColor: 'var(--v2-bg)',
                  borderRadius: 'var(--radius-md)',
                  padding: '20px',
                  marginBottom: '16px',
                  border: '1px solid var(--v2-border)'
                }}>
                  <h4 style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--v2-text)',
                    margin: '0 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: 'var(--radius-full)',
                      backgroundColor: 'var(--v2-primary)',
                      color: '#fff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 700
                    }}>!</span>
                    Add These DNS Records in Your Domain Provider
                  </h4>

                  <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)', margin: '0 0 16px 0', lineHeight: 1.5 }}>
                    Log in to your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.) and add these two CNAME records. Both are required.
                  </p>

                  {/* DNS Record 1 */}
                  <div style={{
                    backgroundColor: 'var(--v2-surface)',
                    borderRadius: 'var(--radius-md)',
                    padding: '16px',
                    marginBottom: '12px',
                    border: '1px solid var(--v2-border)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: 'var(--radius-full)',
                        backgroundColor: 'var(--v2-primary-light)',
                        color: 'var(--v2-primary)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.6875rem',
                        fontWeight: 700
                      }}>1</span>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                        Main Domain Record
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--v2-text-tertiary)' }}>
                        — Routes traffic to your portal
                      </span>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px 0', color: 'var(--v2-text-muted)', width: '100px', verticalAlign: 'top' }}>Type</td>
                          <td style={{ padding: '8px 0' }}>
                            <code style={{
                              backgroundColor: 'var(--v2-surface-alt)',
                              padding: '4px 8px',
                              borderRadius: 'var(--radius-sm)',
                              fontFamily: 'var(--font-mono)',
                              fontWeight: 600,
                              color: 'var(--v2-text)'
                            }}>CNAME</code>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px 0', color: 'var(--v2-text-muted)', verticalAlign: 'top' }}>Host / Name</td>
                          <td style={{ padding: '8px 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <code style={{
                                backgroundColor: 'var(--v2-surface-alt)',
                                padding: '6px 10px',
                                borderRadius: 'var(--radius-sm)',
                                fontFamily: 'var(--font-mono)',
                                color: 'var(--v2-text)',
                                fontSize: '0.8125rem'
                              }}>
                                {existingDomain.dns_instructions.main_cname.name.split('.')[0]}
                              </code>
                              <button
                                onClick={() => copyToClipboard(existingDomain.dns_instructions!.main_cname.name.split('.')[0], 'Host')}
                                onMouseEnter={() => setHoveredButton('copy-main-host')}
                                onMouseLeave={() => setHoveredButton(null)}
                                style={copyButtonStyle(hoveredButton === 'copy-main-host')}
                              >
                                <Copy size={12} /> Copy
                              </button>
                              <span style={{ fontSize: '0.75rem', color: 'var(--v2-text-tertiary)' }}>
                                (or enter full: {existingDomain.dns_instructions.main_cname.name})
                              </span>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px 0', color: 'var(--v2-text-muted)', verticalAlign: 'top' }}>Value / Target</td>
                          <td style={{ padding: '8px 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <code style={{
                                backgroundColor: 'var(--v2-primary-light)',
                                padding: '6px 10px',
                                borderRadius: 'var(--radius-sm)',
                                fontFamily: 'var(--font-mono)',
                                color: 'var(--v2-primary)',
                                fontSize: '0.8125rem',
                                fontWeight: 500
                              }}>
                                {existingDomain.dns_instructions.main_cname.target}
                              </code>
                              <button
                                onClick={() => copyToClipboard(existingDomain.dns_instructions!.main_cname.target, 'Value')}
                                onMouseEnter={() => setHoveredButton('copy-main-value')}
                                onMouseLeave={() => setHoveredButton(null)}
                                style={copyButtonStyle(hoveredButton === 'copy-main-value')}
                              >
                                <Copy size={12} /> Copy
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* DNS Record 2 */}
                  {existingDomain.dns_instructions.acme_cname && (
                    <div style={{
                      backgroundColor: 'var(--v2-surface)',
                      borderRadius: 'var(--radius-md)',
                      padding: '16px',
                      border: '1px solid var(--v2-border)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <span style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: 'var(--radius-full)',
                          backgroundColor: 'var(--v2-primary-light)',
                          color: 'var(--v2-primary)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.6875rem',
                          fontWeight: 700
                        }}>2</span>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                          SSL Certificate Record
                        </span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--v2-text-tertiary)' }}>
                          — Required for HTTPS
                        </span>
                      </div>

                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                        <tbody>
                          <tr>
                            <td style={{ padding: '8px 0', color: 'var(--v2-text-muted)', width: '100px', verticalAlign: 'top' }}>Type</td>
                            <td style={{ padding: '8px 0' }}>
                              <code style={{
                                backgroundColor: 'var(--v2-surface-alt)',
                                padding: '4px 8px',
                                borderRadius: 'var(--radius-sm)',
                                fontFamily: 'var(--font-mono)',
                                fontWeight: 600,
                                color: 'var(--v2-text)'
                              }}>CNAME</code>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px 0', color: 'var(--v2-text-muted)', verticalAlign: 'top' }}>Host / Name</td>
                            <td style={{ padding: '8px 0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <code style={{
                                  backgroundColor: 'var(--v2-surface-alt)',
                                  padding: '6px 10px',
                                  borderRadius: 'var(--radius-sm)',
                                  fontFamily: 'var(--font-mono)',
                                  color: 'var(--v2-text)',
                                  fontSize: '0.8125rem'
                                }}>
                                  {existingDomain.dns_instructions.acme_cname.name.replace(`.${existingDomain.domain.split('.').slice(-2).join('.')}`, '')}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(existingDomain.dns_instructions!.acme_cname!.name.replace(`.${existingDomain.domain.split('.').slice(-2).join('.')}`, ''), 'Host')}
                                  onMouseEnter={() => setHoveredButton('copy-acme-host')}
                                  onMouseLeave={() => setHoveredButton(null)}
                                  style={copyButtonStyle(hoveredButton === 'copy-acme-host')}
                                >
                                  <Copy size={12} /> Copy
                                </button>
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--v2-text-tertiary)', marginTop: '4px' }}>
                                Full name: {existingDomain.dns_instructions.acme_cname.name}
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px 0', color: 'var(--v2-text-muted)', verticalAlign: 'top' }}>Value / Target</td>
                            <td style={{ padding: '8px 0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <code style={{
                                  backgroundColor: 'var(--v2-primary-light)',
                                  padding: '6px 10px',
                                  borderRadius: 'var(--radius-sm)',
                                  fontFamily: 'var(--font-mono)',
                                  color: 'var(--v2-primary)',
                                  fontSize: '0.8125rem',
                                  fontWeight: 500,
                                  wordBreak: 'break-all'
                                }}>
                                  {existingDomain.dns_instructions.acme_cname.target}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(existingDomain.dns_instructions!.acme_cname!.target, 'Value')}
                                  onMouseEnter={() => setHoveredButton('copy-acme-value')}
                                  onMouseLeave={() => setHoveredButton(null)}
                                  style={copyButtonStyle(hoveredButton === 'copy-acme-value')}
                                >
                                  <Copy size={12} /> Copy
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Help text */}
                  <div style={{
                    marginTop: '16px',
                    padding: '12px 14px',
                    backgroundColor: 'var(--v2-warning-bg)',
                    borderRadius: 'var(--radius-sm)',
                    borderLeft: '4px solid var(--v2-warning)',
                    fontSize: '0.8125rem',
                    color: '#92400e',
                    lineHeight: 1.5
                  }}>
                    <strong>Note:</strong> DNS changes can take 5-30 minutes to propagate. After adding both records, click "Check Status" to verify. We'll automatically issue your SSL certificate once DNS is validated.
                  </div>
                </div>
              )}

              {/* Status Details for Active Domain */}
              {existingDomain.status === 'active' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{
                    backgroundColor: 'var(--v2-success-bg)',
                    borderRadius: 'var(--radius-md)',
                    padding: '14px 16px',
                    border: '1px solid var(--v2-success)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Check size={16} strokeWidth={2} color="var(--v2-success)" />
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--v2-success)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>DNS Verified</span>
                    </div>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text)' }}>
                      {existingDomain.dns_validated_at 
                        ? new Date(existingDomain.dns_validated_at).toLocaleDateString()
                        : 'Verified'}
                    </span>
                  </div>
                  <div style={{
                    backgroundColor: 'var(--v2-success-bg)',
                    borderRadius: 'var(--radius-md)',
                    padding: '14px 16px',
                    border: '1px solid var(--v2-success)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Check size={16} strokeWidth={2} color="var(--v2-success)" />
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--v2-success)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SSL Active</span>
                    </div>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text)' }}>
                      {existingDomain.ssl_issued_at 
                        ? new Date(existingDomain.ssl_issued_at).toLocaleDateString()
                        : 'Issued'}
                    </span>
                  </div>
                </div>
              )}

              {/* Validation Errors */}
              {existingDomain.validation_errors && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-md)',
                  borderLeft: existingDomain.validation_errors.includes('waiting for SSL') ? '4px solid var(--v2-warning)' : '4px solid var(--v2-error)',
                  backgroundColor: existingDomain.validation_errors.includes('waiting for SSL') ? 'var(--v2-warning-bg)' : 'var(--v2-error-bg)',
                  color: existingDomain.validation_errors.includes('waiting for SSL') ? '#92400e' : 'var(--v2-error)',
                  fontSize: '0.8125rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  {existingDomain.validation_errors.includes('waiting for SSL') ? (
                    <Clock size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
                  ) : (
                    <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
                  )}
                  <span>{existingDomain.validation_errors}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!hasExistingDomain && (
        <div style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          padding: '48px 24px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: 'var(--radius-full)',
            backgroundColor: 'var(--v2-surface-alt)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <Globe size={32} strokeWidth={1} color="var(--v2-text-muted)" />
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 8px 0' }}>
            No Custom Domain Configured
          </h3>
          <p style={{ color: 'var(--v2-text-tertiary)', margin: 0, fontSize: '0.875rem', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
            Add a custom domain above to give your clients a branded portal experience.
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && deleteModal.domain && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDeleteModal();
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--v2-surface)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              border: '1px solid var(--v2-border)',
              maxWidth: '480px',
              width: '90%',
              overflow: 'hidden',
              fontFamily: 'var(--font-sans)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--v2-border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--v2-error-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <AlertTriangle size={20} strokeWidth={1.5} color="var(--v2-error)" />
                </div>
                <div>
                  <h2 style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: 'var(--v2-text)',
                    margin: 0,
                  }}>
                    Remove Custom Domain
                  </h2>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--v2-error)',
                    margin: '2px 0 0 0',
                    fontWeight: 500,
                  }}>
                    This action cannot be undone
                  </p>
                </div>
              </div>
              <button
                onClick={closeDeleteModal}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--v2-text-muted)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
                  e.currentTarget.style.color = 'var(--v2-text)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--v2-text-muted)';
                }}
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '24px' }}>
              <div style={{
                backgroundColor: 'var(--v2-surface-alt)',
                border: '1px solid var(--v2-border)',
                borderRadius: 'var(--radius-md)',
                padding: '16px',
                marginBottom: '20px',
              }}>
                <p style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: 'var(--v2-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  margin: '0 0 6px 0',
                }}>
                  Domain to Remove
                </p>
                <p style={{
                  fontSize: '1.0625rem',
                  fontWeight: 600,
                  color: 'var(--v2-text)',
                  margin: 0,
                  fontFamily: 'var(--font-mono)',
                }}>
                  {deleteModal.domain.domain}
                </p>
              </div>

              <p style={{
                fontSize: '0.875rem',
                color: 'var(--v2-text-secondary)',
                margin: '0 0 16px 0',
                lineHeight: 1.5,
              }}>
                This will remove the custom domain from your agency portal. Your clients will no longer be able to access the portal via this domain.
              </p>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  color: 'var(--v2-text)',
                  marginBottom: '8px',
                }}>
                  Type{' '}
                  <strong style={{
                    fontFamily: 'var(--font-mono)',
                    backgroundColor: 'var(--v2-bg)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                  }}>
                    DELETE
                  </strong>{' '}
                  to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  disabled={deleting}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: 'var(--v2-surface)',
                    border: `1px solid ${deleteConfirmText !== '' && !isDeleteConfirmValid ? 'var(--v2-error)' : 'var(--v2-border)'}`,
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.875rem',
                    color: 'var(--v2-text)',
                    fontFamily: 'var(--font-mono)',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    if (deleteConfirmText === '' || isDeleteConfirmValid) {
                      e.target.style.borderColor = 'var(--v2-primary)';
                      e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
                    }
                  }}
                  onBlur={(e) => {
                    if (deleteConfirmText === '' || isDeleteConfirmValid) {
                      e.target.style.borderColor = 'var(--v2-border)';
                    }
                    e.target.style.boxShadow = 'none';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDeleteConfirmValid) {
                      handleDeleteDomain();
                    }
                  }}
                />
                {deleteConfirmText !== '' && !isDeleteConfirmValid && (
                  <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', margin: '6px 0 0 0' }}>
                    Please type DELETE to confirm
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--v2-border)',
              backgroundColor: 'var(--v2-surface-alt)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}>
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                onMouseEnter={() => setHoveredButton('cancelDelete')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: hoveredButton === 'cancelDelete' && !deleting ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
                  color: hoveredButton === 'cancelDelete' && !deleting ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.5 : 1,
                  transition: 'all 0.15s',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteDomain}
                disabled={!isDeleteConfirmValid || deleting}
                onMouseEnter={() => setHoveredButton('confirmDelete')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: !isDeleteConfirmValid || deleting
                    ? '#fecaca'
                    : hoveredButton === 'confirmDelete'
                      ? '#b91c1c'
                      : 'var(--v2-error)',
                  color: !isDeleteConfirmValid || deleting ? '#f87171' : '#ffffff',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: !isDeleteConfirmValid || deleting ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {deleting ? (
                  <>
                    <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                    Removing...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} strokeWidth={1.5} />
                    Remove Domain
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AgencyDomainsTab;
